import { SeeqNames } from '@seeqdev/constants';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type {
  DerivedFeatureWithColumnDefinition,
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  TemplateDerivedFeature,
  TemplateInput,
  TemplateOutput,
  TemplatePayload,
} from '@/tools/models/template.types';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

/** State fields derived from a TemplateOutputV1 API response. */
export interface TemplateOutputStateFields {
  name: string;
  id: string;
  versionId: string;
  description: string;
  inputFeatures: TemplateInput[];
  derivedFeatures: TemplateOutput[] | ColumnDefinition[] | DerivedFeatureV1[] | DerivedFeatureWithColumnDefinition[];
  scopedTo: string | undefined;
  datasourceId: string | undefined;
  existingDerivedFeatures: TemplateDerivedFeature[];
  existingInputFeatures: TemplateInput[];
  instanceNameFeature: TemplateInput | undefined;
  newInputFeatures: NewlyAddedInputFeature[];
  newDerivedFeatures: NewlyAddedDerivedFeature[];
}

/**
 * Derives store state fields from a TemplateOutputV1 API response.
 * Use with Object.assign in a store set() call:
 *   set((draft) => { Object.assign(draft, createTemplateOutput(templateOutput)); })
 */
export function createTemplateOutput(templateOutput: TemplateOutputV1): TemplateOutputStateFields {
  const { description, name, id, inputFeatures, derivedFeatures, scopedTo, versionId, datasourceId } = templateOutput;
  return {
    name,
    id,
    versionId: versionId ?? '',
    description: description ?? '',
    inputFeatures: (inputFeatures ?? []) as TemplateInput[],
    derivedFeatures: derivedFeatures ?? [],
    scopedTo,
    datasourceId,
    existingDerivedFeatures: (derivedFeatures ?? []) as TemplateDerivedFeature[],
    existingInputFeatures: (inputFeatures?.filter(
      (feature) => feature.name !== SeeqNames.Models.InstanceNameInputFeatureName,
    ) ?? []) as TemplateInput[],
    instanceNameFeature: inputFeatures?.find(
      (input) => input.name === SeeqNames.Models.InstanceNameInputFeatureName,
    ) as TemplateInput | undefined,
    newInputFeatures: [],
    newDerivedFeatures: [],
  };
}

/** State fields derived from a partial TemplatePayload. Only defined fields are included. */
export type TemplatePayloadStateFields = Partial<
  Pick<
    TemplateOutputStateFields,
    | 'name'
    | 'description'
    | 'inputFeatures'
    | 'derivedFeatures'
    | 'existingDerivedFeatures'
    | 'existingInputFeatures'
    | 'newInputFeatures'
    | 'newDerivedFeatures'
  >
>;

/**
 * Derives a partial state update from a TemplatePayload.
 * Only fields present in the payload are included in the returned object.
 * Preserves SEEQ_MODEL_INSTANCE_NAME when inputFeatures are updated.
 *
 * Use with Object.assign in a store set() call:
 *   set((draft) => { Object.assign(draft, createTemplatePayloadUpdate(template, draft.inputFeatures)); })
 */
export function createTemplatePayloadUpdate(
  template: TemplatePayload,
  currentInputFeatures: TemplateInput[],
): TemplatePayloadStateFields {
  const {
    description,
    name,
    inputFeatures,
    derivedFeatures,
    existingDerivedFeatures,
    newInputFeatures,
    newDerivedFeatures,
    existingInputFeatures,
  } = template;

  const update: TemplatePayloadStateFields = {};
  if (description !== undefined) update.description = description;
  if (name !== undefined) update.name = name;
  if (derivedFeatures !== undefined) update.derivedFeatures = derivedFeatures;
  if (existingDerivedFeatures !== undefined) update.existingDerivedFeatures = existingDerivedFeatures;
  if (existingInputFeatures !== undefined) update.existingInputFeatures = existingInputFeatures;
  if (newInputFeatures !== undefined) update.newInputFeatures = newInputFeatures;
  if (newDerivedFeatures !== undefined) update.newDerivedFeatures = newDerivedFeatures;

  if (inputFeatures !== undefined) {
    const newFeatures = inputFeatures ?? [];
    // CRAB-53616: Ensure the instance name input feature is preserved when updating input features
    const instanceName = currentInputFeatures.find((f) => f.name === SeeqNames.Models.InstanceNameInputFeatureName);
    update.inputFeatures =
      instanceName && !newFeatures.some((f) => f.name === SeeqNames.Models.InstanceNameInputFeatureName)
        ? [...newFeatures, instanceName]
        : newFeatures;
  }

  return update;
}
